'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'

import {
    HOLIDAY_PACKAGE_SORT,
    type HolidayPackageSort,
    type HolidayRegion
} from '@/src/constants/holidays'
import type { HolidayPackageGridSection } from '@/src/types/holidays'
import useClevertapForHolidays from '@/src/hooks/analytics/clevertap/holidays'
import { HOLIDAY_ACTION, HOLIDAY_ACTION_TYPE } from '@/src/utils/analytics/holidays/holidayEvents'
import { buildHolidayRegionSummaries, filterHolidayPackagesByRegion } from '@/src/utils/holidays/holidayRegions'
import {
    EMPTY_HOLIDAY_PACKAGE_FILTERS,
    buildThemeFacets,
    filterPackagesByTheme,
    toggleFilterValue
} from '@/src/utils/holidays/packageFilters'
import { searchHolidayPackages } from '@/src/utils/holidays/searchHolidayPackages'
import { sortHolidayPackages } from '@/src/utils/holidays/sortHolidayPackages'

import HolidaySection from '../holidaySection/holidaySection'
import PackageSearchWrapper from '../packageSearch'
import PackageSortControlWrapper from '../packageSortControl'
import PackageThemeChipsWrapper from '../packageThemeChips'
import RegionFilterWrapper from '../regionFilter'

import PackageGrid from './packageGrid'

interface PackageGridWrapperProps {
    section: HolidayPackageGridSection
    collectionSlug: string
    isMobile: boolean
    variant: 'card' | 'plain' | 'flat'
    isFirst: boolean
}

/** A single region would make the filter a no-op; the sort still shows on its own. */
const MIN_REGIONS_FOR_FILTER = 2

/** Same reasoning as the regions: one chip filters nothing, so the row is not worth the space. */
const MIN_THEMES_FOR_FILTER = 2

/** A search is only worth offering once there are more cards than a reader can scan at a glance. */
const MIN_PACKAGES_FOR_SEARCH = 6

/** One event per pause in typing, rather than one per keystroke. */
const SEARCH_EVENT_DELAY_MS = 500

/** Enough destinations to show the field is live, few enough that one comes round again soon. */
const MAX_SEARCH_HINTS = 6

const PackageGridWrapper: React.FC<PackageGridWrapperProps> = ({
    section,
    collectionSlug,
    isMobile,
    variant,
    isFirst
}) => {
    const locale = useLocale()
    const t = useTranslations('holidaysPage')
    const { triggerHolidayUIActionEvent } = useClevertapForHolidays()
    const [sort, setSort] = useState<HolidayPackageSort>(HOLIDAY_PACKAGE_SORT.PRICE_LOW_TO_HIGH)
    const [selectedRegions, setSelectedRegions] = useState<HolidayRegion[]>([])
    const [selectedThemes, setSelectedThemes] = useState<string[]>([])
    const [query, setQuery] = useState('')

    const allPackages = useMemo(() => section.packages ?? [], [section.packages])
    // Built from the full set, so the chips and their counts hold still while the reader types.
    const regionSummaries = useMemo(() => buildHolidayRegionSummaries(allPackages), [allPackages])
    // Built from the whole collection, so a chip never disappears because of the reader's own pick.
    const themeFacets = useMemo(() => buildThemeFacets(allPackages), [allPackages])
    const sortedPackages = useMemo(() => sortHolidayPackages(allPackages, sort, locale), [allPackages, sort, locale])

    const visiblePackages = useMemo(() => {
        const byRegion = selectedRegions.length
            ? filterHolidayPackagesByRegion(sortedPackages, selectedRegions)
            : sortedPackages
        return searchHolidayPackages(filterPackagesByTheme(byRegion, selectedThemes), query)
    }, [selectedRegions, selectedThemes, sortedPackages, query])

    // The grid follows every keystroke; the event waits for the reader to stop.
    useEffect(() => {
        const trimmed = query.trim()
        if (!trimmed) return undefined

        const timer = window.setTimeout(() => {
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTENT_ENGAGED, {
                action_type: HOLIDAY_ACTION_TYPE.CONTENT,
                component: 'package_search',
                engagement_type: 'search',
                component_label: trimmed,
                result_count: visiblePackages.length
            })
        }, SEARCH_EVENT_DELAY_MS)

        // `visiblePackages` is read for the count only; it is not a dependency, or a query that
        // changes the result would fire the event twice.
        return () => window.clearTimeout(timer)
    }, [query, triggerHolidayUIActionEvent])

    const handleRegionToggle = useCallback(
        (region: HolidayRegion) => {
            const isSelected = selectedRegions.includes(region)
            const next = isSelected ? selectedRegions.filter(item => item !== region) : [...selectedRegions, region]
            setSelectedRegions(next)
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTENT_ENGAGED, {
                action_type: HOLIDAY_ACTION_TYPE.CONTENT,
                component: 'region_filter',
                engagement_type: isSelected ? 'deselect' : 'select',
                component_label: region,
                selected_regions: next.join(',') || 'N/A'
            })
        },
        [selectedRegions, triggerHolidayUIActionEvent]
    )

    const handleThemeToggle = useCallback(
        (key: string) => {
            const isSelected = selectedThemes.includes(key)
            const next = toggleFilterValue(selectedThemes, key)
            setSelectedThemes(next)
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTENT_ENGAGED, {
                action_type: HOLIDAY_ACTION_TYPE.CONTENT,
                component: 'theme_filter',
                engagement_type: isSelected ? 'deselect' : 'select',
                component_label: key,
                selected_themes: next.join(',') || 'N/A'
            })
        },
        [selectedThemes, triggerHolidayUIActionEvent]
    )

    // Real destinations, in the grid's own order, so the hint always names something on the page.
    const searchHints = useMemo(
        () => Array.from(new Set(sortedPackages.map(item => item.destinationName))).slice(0, MAX_SEARCH_HINTS),
        [sortedPackages]
    )

    const handleSearchClear = useCallback(() => setQuery(''), [])

    /** Clears every narrowing the grid applies, so the empty state always has a way out. */
    const handleClearAll = useCallback(() => {
        setQuery('')
        setSelectedThemes([])
        setSelectedRegions([])
    }, [])

    const handleRegionClear = useCallback(() => {
        setSelectedRegions([])
        triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTENT_ENGAGED, {
            action_type: HOLIDAY_ACTION_TYPE.CONTENT,
            component: 'region_filter',
            engagement_type: 'clear'
        })
    }, [triggerHolidayUIActionEvent])

    if (!allPackages.length) return null

    return (
        <HolidaySection
            id={section.id}
            title={section.title}
            isMobile={isMobile}
            variant={variant}
            isFirst={isFirst}
            titleAction={
                allPackages.length >= MIN_PACKAGES_FOR_SEARCH ? (
                    <PackageSearchWrapper
                        id={`${section.id}-search`}
                        value={query}
                        suggestions={searchHints}
                        onChange={setQuery}
                        onClear={handleSearchClear}
                        isMobile={isMobile}
                    />
                ) : undefined
            }
            beforeTitle={
                <>
                    {themeFacets.length >= MIN_THEMES_FOR_FILTER && (
                        <Box sx={{ mb: 2 }}>
                            <PackageThemeChipsWrapper
                                themeFacets={themeFacets}
                                filters={{ ...EMPTY_HOLIDAY_PACKAGE_FILTERS, themes: selectedThemes }}
                                onToggle={handleThemeToggle}
                                isMobile={isMobile}
                            />
                        </Box>
                    )}
                    <RegionFilterWrapper
                        summaries={regionSummaries.length >= MIN_REGIONS_FOR_FILTER ? regionSummaries : []}
                        selected={selectedRegions}
                        onToggle={handleRegionToggle}
                        onClear={handleRegionClear}
                        action={<PackageSortControlWrapper value={sort} onChange={setSort} isMobile={isMobile} />}
                        isMobile={isMobile}
                    />
                </>
            }
        >
            {visiblePackages.length ? (
                <PackageGrid
                    packages={visiblePackages}
                    collectionSlug={collectionSlug}
                    sourceSection={section.id}
                    isMobile={isMobile}
                />
            ) : (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                    {/*
                     * The chips and regions can empty the grid with nothing typed, which used to
                     * read as: No packages match "". Name whichever the reader actually used.
                     */}
                    <Typography sx={{ fontSize: '0.938rem', color: 'textColors.subheading' }}>
                        {query.trim() ? t('searchNoResults', { query: query.trim() }) : t('packagesEmptyTitle')}
                    </Typography>
                    <Button onClick={handleClearAll} sx={{ mt: 1, fontWeight: 700 }}>
                        {query.trim() ? t('clearSearch') : t('filtersClearAll')}
                    </Button>
                </Box>
            )}
        </HolidaySection>
    )
}

export { PackageGridWrapper }
export default PackageGridWrapper
